/**
 * PlateBoardView — kanban board for the Plate page. Columns are driven by the
 * active group-by (status / priority = drag-reorderable; assignee / entity =
 * read-only groupings, since those aren't single-valued fields you can set by
 * a drop). Uses native HTML5 drag-and-drop (the app's established pattern) with
 * framer-motion `layout` on the cards for reflow.
 *
 * The board always shows done tasks (a kanban needs its Done column populated);
 * the "Show done" toggle only affects the list view.
 */

import { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { TodoItem, TaskStatus, TaskPriority, TaskEntityKind } from '@/types/schema';
import {
  STATUS_ORDER,
  PRIORITY_ORDER,
  getStatusTheme,
  getPriorityTheme,
  getEntityTheme,
} from '@/config/entityThemes';
import { updateTaskStatus, updateTaskPriority } from '@/services/todo.service';
import type { AssigneeGroup } from '@/hooks/useTasks';
import { PlateCard } from './PlateCard';
import { Avatar } from '@/components/common/Avatar';
import type { GroupBy } from './PlateListView';

interface Column {
  key: string;
  label: string;
  dotClass: string;
  accentBar: string;
  items: TodoItem[];
  avatar?: { name: string; userId: string };
}

interface PlateBoardViewProps {
  groupBy: GroupBy;
  byStatus: Record<TaskStatus, TodoItem[]>;
  byPriority: Record<TaskPriority, TodoItem[]>;
  byEntity: Record<TaskEntityKind, TodoItem[]>;
  byAssignee: AssigneeGroup[];
  actor: { id: string; name: string };
  onToggleComplete: (task: TodoItem, done: boolean) => void;
  onOpen?: (task: TodoItem) => void;
}

export function PlateBoardView({
  groupBy,
  byStatus,
  byPriority,
  byEntity,
  byAssignee,
  actor,
  onToggleComplete,
  onOpen,
}: PlateBoardViewProps) {
  const [dragTask, setDragTask] = useState<TodoItem | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);

  const canDrag = groupBy === 'status' || groupBy === 'priority';
  const columns = buildColumns(groupBy, { byStatus, byPriority, byEntity, byAssignee });

  const handleDrop = (columnKey: string) => {
    setOverKey(null);
    const task = dragTask;
    setDragTask(null);
    if (!task?.id) return;

    if (groupBy === 'status' && task.status !== columnKey) {
      updateTaskStatus(task.id, columnKey as TaskStatus, actor, task.status).catch(() => {});
    } else if (groupBy === 'priority' && task.priority !== columnKey) {
      updateTaskPriority(task.id, columnKey as TaskPriority, actor).catch(() => {});
    }
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-thin">
      {columns.map((col) => {
        const isOver = canDrag && overKey === col.key;
        return (
          <div
            key={col.key}
            onDragOver={(e) => {
              if (!canDrag) return;
              e.preventDefault();
              if (overKey !== col.key) setOverKey(col.key);
            }}
            onDragLeave={() => {
              if (overKey === col.key) setOverKey(null);
            }}
            onDrop={() => handleDrop(col.key)}
            className={`flex-shrink-0 w-[280px] rounded-2xl border transition-colors ${
              isOver
                ? 'border-brand-secondary/50 bg-brand-secondary/[0.06]'
                : 'border-theme bg-surface-raised/40'
            }`}
          >
            {/* Column header */}
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-theme-soft">
              {col.avatar ? (
                <Avatar name={col.avatar.name} userId={col.avatar.userId} size="xs" />
              ) : (
                <span className={`w-2.5 h-2.5 rounded-full ${col.dotClass}`} />
              )}
              <span className="text-xs font-bold text-theme-primary truncate">{col.label}</span>
              <span className="ml-auto text-[11px] font-semibold text-theme-muted bg-surface-card px-2 py-0.5 rounded-full">
                {col.items.length}
              </span>
            </div>

            {/* Cards */}
            <div className="p-2 space-y-2 min-h-[80px]">
              <AnimatePresence initial={false}>
                {col.items.map((task) =>
                  canDrag ? (
                    <div
                      key={task.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', task.id || '');
                        setDragTask(task);
                      }}
                      onDragEnd={() => {
                        setDragTask(null);
                        setOverKey(null);
                      }}
                      className={dragTask?.id === task.id ? 'opacity-40' : ''}
                    >
                      <PlateCard
                        task={task}
                        onToggleComplete={onToggleComplete}
                        onOpen={onOpen}
                        grabbable
                      />
                    </div>
                  ) : (
                    <PlateCard
                      key={task.id}
                      task={task}
                      onToggleComplete={onToggleComplete}
                      onOpen={onOpen}
                    />
                  ),
                )}
              </AnimatePresence>
              {col.items.length === 0 && (
                <p className="text-[11px] text-theme-muted/60 text-center py-4">
                  {dragTask && canDrag ? 'Drop here' : 'No tasks'}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function buildColumns(
  groupBy: GroupBy,
  data: {
    byStatus: Record<TaskStatus, TodoItem[]>;
    byPriority: Record<TaskPriority, TodoItem[]>;
    byEntity: Record<TaskEntityKind, TodoItem[]>;
    byAssignee: AssigneeGroup[];
  },
): Column[] {
  if (groupBy === 'status') {
    return STATUS_ORDER.map((s) => {
      const t = getStatusTheme(s);
      return { key: s, label: t.label, dotClass: t.dot, accentBar: t.accentBar, items: data.byStatus[s] ?? [] };
    });
  }
  if (groupBy === 'priority') {
    return PRIORITY_ORDER.map((p) => {
      const t = getPriorityTheme(p);
      return { key: p, label: t.label, dotClass: t.dot, accentBar: t.accentBar, items: data.byPriority[p] ?? [] };
    });
  }
  if (groupBy === 'assignee') {
    return data.byAssignee.map((g) => ({
      key: g.userId,
      label: g.userName,
      dotClass: 'bg-slate-400',
      accentBar: 'bg-slate-400',
      items: g.tasks,
      avatar: { name: g.userName, userId: g.userId },
    }));
  }
  return (Object.keys(data.byEntity) as TaskEntityKind[])
    .map((k) => {
      const t = getEntityTheme(k);
      return { key: k, label: t.label, dotClass: t.dot, accentBar: t.accentBar, items: data.byEntity[k] ?? [] };
    })
    .sort((a, b) => b.items.length - a.items.length);
}
